import {
  Suspense,
  lazy,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Outlet,
  createBrowserRouter,
  useLocation,
  useNavigate,
} from "react-router";
import { useTranslation } from "react-i18next";
import { type ApplicationFrameConfig, type FrameItem } from "@inteacc/app-shell";
import {
  ApplicationContextProvider,
  ApplicationExperienceShell,
  ApplicationState,
  RecoveryBoundary,
  UnsavedChangesProvider,
  resolveApplicationNavigation,
  useUnsavedChanges,
  type ApplicationNavigationGroup,
  type ResolvedApplicationNavigationGroup,
} from "@inteacc/application-experience";
import {
  contractVersion,
  type SessionContext,
} from "@inteacc/contracts";
import { noOpTelemetry } from "@inteacc/observability";
import { Button, DensitySelect, IafCopyProvider } from "@inteacc/ui";
import { useDisplayPreferences } from "./display";

const OverviewPage = lazy(() => import("./OverviewPage"));
const RequestListPage = lazy(() => import("./RequestListPage"));
const RequestDetailPage = lazy(() => import("./RequestDetailPage"));
const MasterDetailShowcase = lazy(() => import("./MasterDetailShowcase"));
const InlineEditingShowcase = lazy(() => import("./InlineEditingShowcase"));
const ThemeStudio = lazy(() => import("./ThemeStudio"));
const ComponentLibrary = lazy(() => import("./ComponentLibrary"));
const VisualizationGallery = lazy(() => import("./VisualizationGallery"));
const StateGallery = lazy(() => import("./StateGallery"));
const BusinessObjectShowcase = lazy(() => import("./BusinessObjectShowcase"));
const ExperienceCenter = lazy(() => import("./ExperienceCenter"));
const EnterprisePatternsShowcase = lazy(() => import("./EnterprisePatternsShowcase"));
const ExperienceEnrichmentShowcase = lazy(() => import("./ExperienceEnrichmentShowcase"));

export const showcaseSession: SessionContext = {
  contractVersion,
  sessionId: "showcase-session",
  subjectId: "showcase-user",
  displayName: "Alex Morgan",
  locale: "en-CA",
  timeZone: "America/Toronto",
  tenant: {
    tenantId: "atlas-demo",
    tenantDisplayName: "Atlas Services",
  },
  activeCompany: {
    companyId: "atlas-ca",
    companyDisplayName: "Atlas Canada",
  },
  availableCompanies: [
    {
      companyId: "atlas-ca",
      companyDisplayName: "Atlas Canada",
    },
    {
      companyId: "atlas-us",
      companyDisplayName: "Atlas USA",
    },
  ],
};

const LoadingRoute = () => (
  <div aria-live="polite" className="showcase-route-loading" role="status">
    Loading pattern…
  </div>
);

const ShowcaseApplicationShell = ({
  children,
  navigation,
  frame,
  onRouteOpen,
}: {
  children: ReactNode;
  navigation: readonly ResolvedApplicationNavigationGroup[];
  frame: ApplicationFrameConfig;
  onRouteOpen?: (href: string) => void;
}) => {
  const navigate = useNavigate();
  const { requestNavigation } = useUnsavedChanges();
  const guardedFrame: ApplicationFrameConfig = {
    ...frame,
    onOpenItem: (item) => {
      requestNavigation(() => frame.onOpenItem?.(item));
    },
  };
  return (
    <ApplicationExperienceShell
      navigation={navigation}
      frame={guardedFrame}
      collapsibleNavigation
      onNavigate={(href) => requestNavigation(() => {
        onRouteOpen?.(href);
        void navigate(href);
      })}
    >
      {children}
    </ApplicationExperienceShell>
  );
};

export const RootLayout = () => {
  const { i18n, t } = useTranslation();
  const { density, setDensity, setTheme, theme } = useDisplayPreferences();
  const { pathname } = useLocation();
  const [currentCompanyId, setCurrentCompanyId] = useState(
    showcaseSession.activeCompany?.companyId ?? "",
  );
  const [locationId, setLocationId] = useState("toronto");
  const [businessUnitId, setBusinessUnitId] = useState("operations");
  const [favoriteIds, setFavoriteIds] = useState<readonly string[]>(["records", "objects"]);
  const [recentIds, setRecentIds] = useState<readonly string[]>(["overview"]);
  const [search, setSearch] = useState("");

  const localizedSession = useMemo(
    () => ({
      ...showcaseSession,
      locale: i18n.language === "fr" ? "fr-CA" : "en-CA",
    }),
    [i18n.language],
  );
  const currentCompany =
    localizedSession.availableCompanies.find(
      (company) => company.companyId === currentCompanyId,
    ) ?? localizedSession.activeCompany;
  const groups: readonly ApplicationNavigationGroup[] = [
    {
      id: "application-frame", label: "Application Frame",
      items: [
        { id: "overview", label: t("overview"), href: "/", iconRole: "overview" },
        { id: "experience-center", label: t("experienceCenter"), href: "/experience-center", iconRole: "master" },
      ],
    },
    {
      id: "component-library", label: "Component Library",
      items: [{ id: "component-catalog", label: "Component Library", href: "/components", iconRole: "master" }],
    },
    {
      id: "dashboard-cards", label: "Dashboard & Cards",
      items: [
        { id: "p2-experience", label: "Experience Enrichment (P2)", href: "/experience-enrichment", iconRole: "overview" },
      ],
    },
    {
      id: "lists-grids", label: "Lists & Grids",
      items: [
        { id: "records", label: t("records"), href: "/records" },
        { id: "inline", label: t("inlineEditing"), href: "/inline-editing" },
      ],
    },
    {
      id: "forms-details", label: "Forms & Detail",
      items: [
        { id: "detail", label: t("detail"), href: "/records/request-1" },
        { id: "master", label: t("masterDetail"), href: "/master-detail" },
      ],
    },
    {
      id: "business-objects", label: "Business Objects",
      items: [{ id: "objects", label: t("businessObjects"), href: "/objects" }],
    },
    {
      id: "visualizations", label: "Visualizations",
      items: [{ id: "visualizations-gallery", label: t("visualizations"), href: "/visualizations",
        iconRole: "visualizations" }],
    },
    {
      id: "themes-appearance", label: "Themes & Appearance",
      items: [{ id: "themes", label: t("themeStudio"), href: "/theme-studio",
        iconRole: "themes", requiredCapability: "theme.preview" }],
    },
    {
      id: "interaction-states", label: "Interaction & States",
      items: [
        { id: "enterprise-ui", label: "Enterprise UI Patterns (P1)", href: "/enterprise-ui", iconRole: "detail" },
        { id: "states", label: t("states"), href: "/states" },
      ],
    },
  ];
  const navigation = resolveApplicationNavigation(groups, {
    capabilities: ["theme.preview", "objects.read", "experience.preview"],
    currentHref: pathname,
  });
  const navigate = useNavigate();
  const entries: readonly FrameItem[] = groups.flatMap((group) =>
    group.items.map((item) => ({ id: item.id, label: item.label, href: item.href })),
  );
  const recordRecentHref = (href: string) => {
    const entry = entries.find((item) => item.href === href);
    if (entry) {
      setRecentIds((current) =>
        current[0] === entry.id ? current : [entry.id, ...current.filter((id) => id !== entry.id)].slice(0, 7));
    }
  };
  const openItem = (item: FrameItem) => {
    if (!item.href) return;
    setRecentIds((current) => [item.id, ...current.filter((id) => id !== item.id)].slice(0, 7));
    void navigate(item.href);
  };
  const appearanceControl = (
    <div className="showcase-frame-appearance">
      <DensitySelect value={density} onChange={setDensity} />
      <Button ariaLabel="Switch theme" onClick={() =>
        setTheme((value) => value === "light" ? "dark" : "light")} tone="neutral">
        {theme === "light" ? "Dark theme" : "Light theme"}
      </Button>
      <Button ariaLabel="Switch language" tone="neutral" onClick={() => {
        void i18n.changeLanguage(i18n.language === "en" ? "fr" : "en");
      }}>{i18n.language === "en" ? "Français" : "English"}</Button>
    </div>
  );
  const frame: ApplicationFrameConfig = {
    logo: <span aria-hidden="true" className="showcase-frame-logo">I</span>,
    environment: "DEMO / Reference data only",
    version: "0.1.0",
    supportHref: "/experience-center",
    searchResults: [
      ...entries,
      { id: "sr-1042", label: "SR-1042 · Restore building access",
        description: "Fictional business object", href: "/objects/service.request/SR-1042" },
      { id: "asset-204", label: "ASSET-204 · Meeting-room display",
        description: "Fictional asset", href: "/objects/asset.item/ASSET-204" },
    ].filter((item) => [item.label, item.description ?? ""]
      .join(" ").toLowerCase().includes(search.toLowerCase())),
    onSearch: setSearch,
    tasks: [
      { id: "task1", label: "Review pending request", href: "/records/request-1", description: "Reference task · due today" },
      { id: "task2", label: "Check allocation", href: "/inline-editing", description: "Reference reminder" },
    ],
    notifications: [
      { id: "alert1", label: "Service review assigned", href: "/objects/service.request/SR-1042", description: "Fictional alert" },
    ],
    favorites: entries.filter((item) => favoriteIds.includes(item.id)),
    favoriteIds,
    recent: recentIds.map((id) => entries.find((item) => item.id === id)).filter((item): item is FrameItem => Boolean(item)),
    ...(entries.find((item) => item.href === pathname)?.id
      ? { activeWorkspaceId: entries.find((item) => item.href === pathname)!.id }
      : {}),
    workspaceTabs: recentIds.slice(0, 4).reverse().map((id) => entries.find((item) => item.id === id)).filter((item): item is FrameItem => Boolean(item)),
    onCloseWorkspace: (id) => setRecentIds((current) => current.filter((value) => value !== id)),
    onToggleFavorite: (id) => setFavoriteIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id]),
    contextSelectors: [
      { label: "Location", value: locationId, onChange: setLocationId,
        options: [{ id: "toronto", label: "Toronto" }, { id: "montreal", label: "Montréal" }] },
      { label: "Business unit", value: businessUnitId, onChange: setBusinessUnitId,
        options: [{ id: "operations", label: "Operations" }, { id: "services", label: "Services" }] },
    ],
    helpLinks: [
      { id: "guide", label: "Framework guidance", href: "/experience-center" },
      { id: "theme-help", label: "Theme Designer", href: "/theme-studio" },
    ],
    profileActions: [
      { id: "profile-view", label: "Profile preview", href: "/experience-center" },
      { id: "profile-theme", label: "Appearance preferences", href: "/theme-studio" },
    ],
    appearanceControl,
    assistantControl: <button type="button" className="showcase-frame-assistant"
      aria-label="AI assistant preview" title="AI assistant preview"
      onClick={() => void navigate("/experience-center")}>✦</button>,
    onOpenItem: openItem,
    showHelp: true,
    showFullscreen: true,
  };


  return (
    <RecoveryBoundary correlationId="SHOWCASE-UI"
      onError={(error) =>
        noOpTelemetry.error(error, { boundary: "framework-showcase" })
      }
    >
      <IafCopyProvider translate={(english) => i18n.t(english, { defaultValue: english })}>
      <ApplicationContextProvider
        application={{
          id: "framework-showcase",
          name: t("applicationName"),
          version: "0.1.0",
        }}
        capabilities={["theme.preview", "objects.read", "experience.preview"]}
        currentCompany={currentCompany}
        screen={{ screenId: pathname, title: t("applicationName") }}
        session={localizedSession}
        switchCompany={setCurrentCompanyId}
      >
        <UnsavedChangesProvider>
          <ShowcaseApplicationShell navigation={navigation} frame={frame} onRouteOpen={recordRecentHref}>
            <Suspense fallback={<LoadingRoute />}>
              <Outlet />
            </Suspense>
          </ShowcaseApplicationShell>
        </UnsavedChangesProvider>
      </ApplicationContextProvider>
      </IafCopyProvider>
    </RecoveryBoundary>
  );
};

export const routes = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: (
      <ApplicationState kind="error" title="Showcase route unavailable"
        message="This reference page could not be displayed.">
        <a href="/">Return to framework overview</a>
      </ApplicationState>
    ),
    children: [
      { index: true, element: <OverviewPage /> },
      { path: "experience-center", element: <ExperienceCenter /> },
      { path: "records", element: <RequestListPage /> },
      { path: "records/new", element: <RequestDetailPage initialMode="create" /> },
      { path: "records/:requestId", element: <RequestDetailPage /> },
      { path: "master-detail", element: <MasterDetailShowcase /> },
      { path: "inline-editing", element: <InlineEditingShowcase /> },
      { path: "theme-studio", element: <ThemeStudio /> },
      { path: "components", element: <ComponentLibrary /> },
      { path: "visualizations", element: <VisualizationGallery /> },
      { path: "states", element: <StateGallery /> },
      { path: "enterprise-ui", element: <EnterprisePatternsShowcase /> },
      { path: "experience-enrichment", element: <ExperienceEnrichmentShowcase /> },
      { path: "objects", element: <BusinessObjectShowcase /> },
      {
        path: "objects/:objectType/:objectId",
        element: <BusinessObjectShowcase />,
      },
      {
        path: "*",
        element: (
          <ApplicationState kind="not-found" title="Pattern not found"
            message="The requested showcase pattern does not exist.">
            <a href="/">Return to the showcase</a>
          </ApplicationState>
        ),
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
